import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class CustomValidators {
  static patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        // if control is empty return no error
        return null;
      }

      // test the value of the control against the regexp supplied
      const valid = regex.test(control.value);

      // if true, return no error (no error), else return error passed in the second parameter
      return valid ? null : error || { pattern: true };
    };
  }

  static mustContainLowerCase(error: ValidationErrors): ValidatorFn {
    return CustomValidators.patternValidator(/[a-z]/, error);
  }

  static mustContainUpperCase(error: ValidationErrors): ValidatorFn {
    return CustomValidators.patternValidator(/[A-Z]/, error);
  }

  static mustContainSpecialCharacter(error: ValidationErrors): ValidatorFn {
    // eslint-disable-next-line no-useless-escape
    return CustomValidators.patternValidator(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/, error);
  }

  static mustContainNumber(error: ValidationErrors): ValidatorFn {
    return CustomValidators.patternValidator(/\d/, error);
  }

  static maxDateValidator(maxDate: Date, error?: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        return null;
      }

      const valid = value <= maxDate;

      return valid ? null : (error ?? { maxDate: true });
    };
  }

  static urlValidator(error?: ValidationErrors): ValidatorFn {
    return CustomValidators.patternValidator(/(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})[/\w .-]*\/?/, error);
  }

  static urlValidatorWithHttp(error?: ValidationErrors): ValidatorFn {
    return CustomValidators.patternValidator(/(http|https):\/\/?([[\da-z.-]+)\.([a-z.]{2,6})[/\w .-]*\/?/, error);
  }

  static multipleWordsContainUnderscore(error?: ValidationErrors): ValidatorFn {
    return CustomValidators.patternValidator(/^[^-s][a-zA-Z0-9_s-]+$/, error);
  }

  static genericValidator(isValid: (value: any) => boolean, error?: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value || isValid(control.value)) {
        return null;
      } else {
        return error || { generic: { value: control.value } };
      }
    };
  }

  static wordsValidator(error?: ValidationErrors): ValidatorFn {
    return CustomValidators.patternValidator(/^[\p{L}\s\-.]+$/u, error);
  }

  static postalCodeValidator(error?: ValidationErrors): ValidatorFn {
    return CustomValidators.patternValidator(/^[A-Z0-9]{1,10}([ -][A-Z0-9]{1,10})?$/u, error);
  }

  static zipCodeValidator(error?: ValidationErrors): ValidatorFn {
    return CustomValidators.patternValidator(/^\d{5}$/, error);
  }

  static phoneValidator(error?: ValidationErrors): ValidatorFn {
    return CustomValidators.patternValidator(/^\+?([0-9\s-()]{7,25})(?:[xX]|\sext|\sX)?(\d{1,6})?$/, error);
  }

  static listValidator<T>(list: T[], param: keyof T, error?: ValidationErrors) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        return null;
      }

      const isFound = list.some((item) => {
        return item[param] === value;
      });

      return isFound ? null : error;
    };
  }

  static noSpacesValidator(error?: ValidationErrors): ValidatorFn {
    return CustomValidators.patternValidator(/^\S*$/, error ?? { noSpaces: true });
  }

  static endDateAfterStartDate(startControlName: string, error?: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const endDate = control.value;
      if (!endDate) {
        return null;
      }
      const startDate = control.parent?.get(startControlName)?.value;
      if (!startDate) {
        return null;
      }
      return endDate >= startDate ? null : (error ?? { endDateBeforeStartDate: true });
    };
  }
}
